<template>
  <div class="preview-calendar-container">
    <FullCalendar
      ref="calendar"
      data-testid="my-calendar.schedule-detail-calendar.preview-calendar"
      class="preview-calendar"
      :options=calendarOptions />
  </div>
</template>

<script setup>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment-timezone';
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex'

const store = useStore()

const props = defineProps({
  disableBtn: {
    type: Boolean,
    default: true,
  },
  activeTab: {
    type: String,
    default: 'appointment',
  },
})

// const currentEventInfo = computed(() => store.state.calendar.currentEventInfo)
const currentEventInfo = computed(() => store.state.calendar.currentEventInfo)
const currentTaskInfo = computed(() => store.state.calendar.currentTaskInfo)
const preScheduleInfo = computed(() => store.state.calendar.preScheduleInfo)
const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])
const eventTypeList = computed(() => store.getters['calendar/rawEventTypeList'] || [])

const getEventBackgroundColor = (event) => {
  const typeName = event?.event_type
  const matched = eventTypeList.value.find(t => t?.name === typeName)
  if (matched?.color_hex) return matched.color_hex

  return '#E1F5FE'
}

const isPreScheduleEvent = (event) => {
  const preEvent = preScheduleInfo.value || {}
  if (!preEvent.start_time || !preEvent.end_time) {
    return false
  }

  if (preEvent.event_id && event.event_id) {
    return preEvent.event_id === event.event_id
  }

  return (
    preEvent.start_time === event.start_time &&
    preEvent.end_time === event.end_time
  )
}

const convertedEventList = computed(() => {
  const eventList = [...store.state.calendar.rawEventList].filter(event => !event?.is_canceled);
  // right calendar pre schedule info
  // console.log(preScheduleInfo.value,'preScheduleInfo')
  const { start_time, end_time, event_id } = preScheduleInfo.value;

  const startMoment = start_time ? moment(start_time) : null;
  const endMoment = end_time ? moment(end_time) : null;
  const isValidPresetTimeRange = Boolean(
    startMoment?.isValid?.() &&
    endMoment?.isValid?.() &&
    endMoment.isAfter(startMoment)
  );

  const presetIndexById = event_id
    ? eventList.findIndex(e => e.event_id === event_id)
    : -1;
  const presetIndexByTime = (start_time && end_time)
    ? eventList.findIndex(e => e.start_time === start_time && e.end_time === end_time)
    : -1;

  const shouldShowPreset = Boolean(
    isValidPresetTimeRange &&
    (
      // new event preview (no event_id) always show preset
      !event_id ||
      // existing event (has event_id): only show preset when user changed something
      props.disableBtn === false
    )
  );

  // for task tab, we temporarily don't show preset event
  if (shouldShowPreset && props.activeTab === 'appointment') {
    const existingIndex = presetIndexById !== -1 ? presetIndexById : presetIndexByTime;
    if (existingIndex !== -1) {
      eventList[existingIndex] = preScheduleInfo.value; // replace event
    } else {
      eventList.push(preScheduleInfo.value); // add event
    }
  }
    
  // console.log(eventList,'eventList')
  return eventList
    .filter(e => e?.start_time && e?.end_time)
    .map(event => {
    return {
      ...event,
      start: event.start_time,
      end: event.end_time,
      backgroundColor: getEventBackgroundColor(event),
      title: event.event_title,
      isPreSchedule: isPreScheduleEvent(event),
    }
  })
});

const calendar = ref(null);

const resolveTargetDate = () => {
  const eventSelectedDate = currentEventInfo.value?.selectedDate
  if (eventSelectedDate && moment(eventSelectedDate, 'YYYY-MM-DD', true).isValid()) {
    return moment.tz(eventSelectedDate, 'YYYY-MM-DD', calendarTimezone.value).toDate()
  }

  const taskSelectedDate = currentTaskInfo.value?.selectedDate
  if (taskSelectedDate && moment(taskSelectedDate, 'YYYY-MM-DD', true).isValid()) {
    return moment.tz(taskSelectedDate, 'YYYY-MM-DD', calendarTimezone.value).toDate()
  }

  const preStart = preScheduleInfo.value?.start_time
  if (preStart && moment(preStart).isValid()) return preStart

  const taskDate = currentTaskInfo.value?.due_date
  if (taskDate && moment(taskDate).isValid()) return taskDate

  const eventStart = currentEventInfo.value?.start || currentEventInfo.value?.start_time
  if (eventStart && moment(eventStart).isValid()) return eventStart

  return null
}

const calendarOptions = ref({
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, momentTimezonePlugin],
  initialView: 'timeGridDay',
  dayHeaderContent: () => '',
  height: '600px',
  // headerToolbar: false,
  headerToolbar: {
    left: 'prev next', 
    center: 'title',
    right: '',
  }, 
  allDaySlot: false,
  timeZone: calendarTimezone.value,
  slotDuration: '00:15:00', 
  slotMinTime: '00:00:00', 
  slotMaxTime: '24:00:00',
  scrollTime: '07:00:00',
  slotLabelFormat: {
    hour: 'numeric',
    minute: '2-digit',
    omitZeroMinute: true,
    meridiem: 'short',
  },
  events: convertedEventList.value,
  // FullCalendar owns the toolbar / event DOM, so testids are attached via its mount hooks.
  viewDidMount: function(info) {
    const calendarEl = info.el.closest('.fc')
    if (!calendarEl) return
    const toolbarTestIds = {
      'fc-prev-button': 'prev-btn',
      'fc-next-button': 'next-btn',
      'fc-toolbar-title': 'toolbar-title',
    }
    Object.entries(toolbarTestIds).forEach(([className, testId]) => {
      const el = calendarEl.querySelector(`.${className}`)
      if (el) el.setAttribute('data-testid', `my-calendar.schedule-detail-calendar.${testId}`)
    })
  },
  eventDidMount: function(info) {
    // The event being scheduled has no event_id yet, so it gets a fixed key.
    const eventKey = info.event.extendedProps.isPreSchedule ? 'pre-schedule' : info.event.extendedProps.event_id
    info.el.setAttribute('data-testid', `my-calendar.schedule-detail-calendar.event.${eventKey}`)
    info.el.style.backgroundColor = info.event.backgroundColor;
    info.el.style.color = '#000';
    info.el.style.borderWidth = '0';
    info.el.style.borderRadius = '2px'
    info.el.style.cursor = 'default'

    if (info.event.extendedProps.isPreSchedule) {
      info.el.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.16)'
      info.el.style.transform = 'scale(1.02)'
      info.el.style.transformOrigin = 'center'
      info.el.style.zIndex = '3'
      info.el.style.borderRadius = '4px'
      info.el.style.overflow = 'visible'
    } else {
      info.el.style.boxShadow = 'none'
      info.el.style.transform = 'none'
      info.el.style.zIndex = '1'
      info.el.style.overflow = 'hidden'
    }
  },

  eventContent: function(arg) {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.justifyContent = 'center';
    container.style.alignItems = 'flex-start'
    container.style.padding = arg.event.extendedProps.isPreSchedule ? '1px 4px 1px 8px' : '0 0 0 3px';
    container.style.height = '100%';
    container.style.width = '100%';
    container.style.boxSizing = 'border-box';
    container.style.position = 'relative';
    container.style.color = '#263238'; // event title color

    
    const eventTime = document.createElement('div');
    const start = moment(arg.event.start).tz(calendarTimezone.value).format('HH:mm');
    const end = moment(arg.event.end).tz(calendarTimezone.value).format('HH:mm');
    const duration = moment.duration(moment(arg.event.end).diff(moment(arg.event.start)));
    const isShortEvent = duration.asHours() < 1;
    const createIndicator = () => {
      const indicator = document.createElement('span');
      indicator.style.position = 'absolute';
      indicator.style.width = '10px';
      indicator.style.height = '10px';
      indicator.style.borderRadius = '9999px';
      indicator.style.backgroundColor = '#FFFFFF';
      indicator.style.border = `1px solid ${arg.event.backgroundColor}`;
      indicator.style.boxSizing = 'border-box';
      return indicator;
    }

    const eventTitle = document.createElement('div');
    eventTitle.innerHTML = arg.event.title;
    eventTitle.style.width = '100%';
    eventTitle.style.textAlign = 'left';
    eventTitle.style.whiteSpace = 'nowrap';
    eventTitle.style.overflow = 'hidden';
    eventTitle.style.textOverflow = 'ellipsis';
    eventTitle.style.fontWeight = arg.event.extendedProps.isPreSchedule ? '600' : '500';

    if (isShortEvent) {
      eventTitle.innerHTML = `${arg.event.title} ${start} - ${end}`;

      container.appendChild(eventTitle);
    } else {
      eventTime.innerHTML = `${start} - ${end}`;
      eventTime.style.textAlign = 'left';
      eventTitle.style.marginBottom = '3px';
      eventTime.style.fontWeight = arg.event.extendedProps.isPreSchedule ? '500' : '400';

      container.appendChild(eventTitle);
      container.appendChild(eventTime);
    }

    if (arg.event.extendedProps.isPreSchedule) {
      const topRightIndicator = createIndicator();
      topRightIndicator.style.top = '-6px';
      topRightIndicator.style.right = '18px';

      const bottomLeftIndicator = createIndicator();
      bottomLeftIndicator.style.bottom = '-6px';
      bottomLeftIndicator.style.left = '20px';

      container.appendChild(topRightIndicator);
      container.appendChild(bottomLeftIndicator);
    }

    return { domNodes: [container] };
  },
});

watch(
  [() => preScheduleInfo.value?.start_time, () => currentEventInfo.value?.selectedDate, () => currentTaskInfo.value?.selectedDate],
  () => {
    if (!calendar.value?.getApi) return
    const target = resolveTargetDate()
    if (!target) return
    calendar.value.getApi().gotoDate(target)
  },
  { immediate: true }
)

watch(
  () => calendar.value,
  (newVal) => {
    if (newVal) {
      updateCalendarView(newVal);
    }
  },
  { immediate: true }  
);


// update pre schedule info after mounted
watch(convertedEventList, (newVal) => {
  // console.log(newVal,'convertedEventListnewVal')
  if (calendar.value) {
    // console.log(newVal,'convertedEventListnewVal')
    calendarOptions.value.events = [...newVal];
    calendarOptions.value.timeZone = calendarTimezone.value;
  }
}, { immediate: true, deep: true })

watch(calendarTimezone, (newVal) => {
  calendarOptions.value.timeZone = newVal;
  if (calendar.value) {
    const calendarApi = calendar.value.getApi();
    calendarApi.setOption('timeZone', newVal);
  }
}, { immediate: true })
// update calendar date after mounted
watch(preScheduleInfo, (newVal) => {
  
  if (calendar.value) {
    // console.log(newVal,'preScheduleInfo')
    const calendarApi = calendar.value.getApi();
    calendarApi.gotoDate(moment.utc(newVal.start_time).tz(calendarTimezone.value).format());
  }
}, { immediate: true, deep: true })


// Helper function to update calendar view
function updateCalendarView(calendarInstance) {
  // console.log('updateCalendarView');
  const calendarApi = calendarInstance.getApi();

  if (preScheduleInfo.value?.start_time) {
    calendarApi.gotoDate(moment.utc(preScheduleInfo.value.start_time).tz(calendarTimezone.value).format());
  }

  calendarApi.render();

}

</script>

<style scoped>
.preview-calendar-container{
  background-color: #FFFFFF;
  border-radius: 2px;
}

:deep(.preview-calendar .fc-event),
:deep(.preview-calendar .fc-timegrid-event),
:deep(.preview-calendar .fc-timegrid-event-harness),
:deep(.preview-calendar .fc-event-main) {
  cursor: default !important;
}
</style>
